import React,{useState,useEffect,useRef}from'react';
import api from'../../services/api';
import{fmtDate}from'../../utils/helpers';

interface Props{
  label?:string;
  onSelect:(admission:any)=>void;
  onClear?:()=>void;
  placeholder?:string;
  statusFilter?:string; // 'ADMITTED' | '' for all
  required?:boolean;
  className?:string;
}

const INP='w-full bg-white border-2 border-slate-200 rounded-xl px-3.5 py-2.5 text-sm text-slate-800 focus:outline-none focus:border-[#02357A] focus:ring-2 focus:ring-[#02357A]/10 transition-all placeholder:text-slate-400';
const LBL='block text-xs font-bold text-slate-500 uppercase tracking-wide mb-1.5';

export default function AdmissionSearchInput({label,onSelect,onClear,placeholder='Search by patient name, UHID or IPD number...',statusFilter='ADMITTED',required,className=''}:Props){
  const[q,setQ]=useState('');
  const[results,setResults]=useState<any[]>([]);
  const[loading,setLoading]=useState(false);
  const[show,setShow]=useState(false);
  const[recent,setRecent]=useState<any[]>([]);
  const ref=useRef<HTMLDivElement>(null);

  useEffect(()=>{
    api.getAdmissions?.({status:statusFilter||undefined,take:8}).then((r:any)=>{
      const list=r?.data?.admissions||r?.data?.data||(Array.isArray(r?.data)?r.data:[]);
      setRecent(Array.isArray(list)?list:[]);
    }).catch(()=>{});
  },[statusFilter]);

  useEffect(()=>{
    const h=(e:MouseEvent)=>{if(ref.current&&!ref.current.contains(e.target as Node))setShow(false);};
    document.addEventListener('mousedown',h);return()=>document.removeEventListener('mousedown',h);
  },[]);

  useEffect(()=>{
    if(q.length<2){setResults([]);return;}
    const t=setTimeout(async()=>{
      setLoading(true);
      try{
        const r=await api.getAdmissions?.({search:q,status:statusFilter||undefined,take:10});
        const list=r?.data?.admissions||r?.data?.data||(Array.isArray(r?.data)?r.data:[]);
        setResults(Array.isArray(list)?list:[]);setShow(true);
      }catch{setResults([]);}
      setLoading(false);
    },300);
    return()=>clearTimeout(t);
  },[q,statusFilter]);

  const pick=(a:any)=>{setQ('');setResults([]);setShow(false);onSelect(a);};
  const display=q.length>=2?results:recent;

  return(
    <div className={className}>
      {label&&<label className={LBL}>{label}{required&&<span className="text-red-500 ml-0.5">*</span>}</label>}
      <div ref={ref} className="relative" style={{isolation:'isolate'}}>
        <div className="relative">
          <span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400">🏥</span>
          <input value={q} onFocus={()=>setShow(true)}
            onChange={e=>{setQ(e.target.value);if(!e.target.value)onClear?.();}}
            placeholder={placeholder} required={required}
            className={`${INP} pl-10 pr-10`}/>
          {loading&&<div className="absolute right-3.5 top-1/2 -translate-y-1/2 w-4 h-4 border-2 border-[#02357A]/20 border-t-[#02357A] rounded-full animate-spin"/>}
          {!loading&&q&&<button type="button" onClick={()=>{setQ('');onClear?.();}} className="absolute right-3.5 top-1/2 -translate-y-1/2 text-slate-300 hover:text-slate-500 text-xl">×</button>}
        </div>

        {show&&(
          <div className="absolute z-[9999] top-full left-0 right-0 mt-1.5 bg-white border-2 border-slate-200 rounded-2xl shadow-2xl max-h-72 overflow-y-auto"
            style={{boxShadow:'0 25px 50px -12px rgba(0,0,0,0.3)'}}>
            <div className="px-4 py-2.5 bg-slate-50 border-b border-slate-100 flex items-center justify-between">
              <span className="text-xs font-bold text-slate-400 uppercase tracking-wider">{q.length<2?'🕐 Recent Admissions':'🔍 Results'}</span>
              <span className="text-xs text-slate-300">{q.length<2?'Type to search':display.length+' found'}</span>
            </div>
            {display.length>0?display.map((a:any)=>{
              const pname=`${a.patientFirstName||a.patient_first_name||a.firstName||''} ${a.patientLastName||a.patient_last_name||a.lastName||''}`.trim();
              return(
                <div key={a.id} onClick={()=>pick(a)}
                  className="flex items-center gap-3 px-4 py-3 hover:bg-blue-50 cursor-pointer border-b border-slate-100 last:border-0 transition-colors group">
                  <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-[#02357A] to-[#039089] flex items-center justify-center text-white font-black text-sm flex-shrink-0">
                    {(pname||'P')[0].toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="font-bold text-sm text-slate-800 group-hover:text-[#02357A]">{pname||'Unknown'}</p>
                      <span className="font-mono text-xs bg-blue-50 text-[#02357A] px-2 py-0.5 rounded-lg border border-blue-100">{a.ipdNumber||a.ipd_number||'—'}</span>
                    </div>
                    <div className="flex gap-2 text-xs text-slate-400 mt-0.5 flex-wrap">
                      <span>UHID: {a.uhid||'—'}</span>
                      <span>·</span><span>Ward: {a.wardName||a.ward_name||'—'}</span>
                      <span>·</span><span>Bed: {a.bedNumber||a.bed_number||'—'}</span>
                      {(a.admissionDate||a.admission_date)&&<><span>·</span><span>{fmtDate(a.admissionDate||a.admission_date)}</span></>}
                    </div>
                  </div>
                  <span className={`text-xs px-2 py-0.5 rounded-full font-bold flex-shrink-0 ${a.status==='ADMITTED'?'bg-emerald-100 text-emerald-700':'bg-slate-100 text-slate-500'}`}>
                    {a.status||'ADMITTED'}
                  </span>
                </div>
              );
            }):(q.length>=2&&!loading&&<div className="p-5 text-center text-sm text-slate-400">No admissions found for "{q}"</div>)}
          </div>
        )}
      </div>
    </div>
  );
}
